import { showActionModal } from "./ui/modal";
import { t } from "./i18n";

/**
 * The Terms-of-Service version a user agrees to when uploading. Bump this when the
 * terms text changes so the server consent record (consents.tos_version) tracks
 * which version each user accepted.
 */
export const TOS_VERSION = "2026-07-03";

const TOS_CONSENT_KEY = "resonella.tosConsent";

/**
 * True when this device has recorded consent to the current TOS_VERSION, so the
 * mic-upload link-open gate can be skipped. A bumped TOS_VERSION no longer
 * matches the stored value, so the gate reappears until consent is recorded again.
 */
export function hasTosConsent(): boolean {
  try {
    const raw = localStorage.getItem(TOS_CONSENT_KEY);
    if (raw === null) {
      return false;
    }
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null && (parsed as Record<string, unknown>).tosVersion === TOS_VERSION;
  } catch {
    return false;
  }
}

/** Records consent to the current TOS_VERSION on this device (UI-friction skip only; the server consent record is sent separately on every upload). */
export function recordTosConsent(): void {
  try {
    localStorage.setItem(TOS_CONSENT_KEY, JSON.stringify({ tosVersion: TOS_VERSION }));
  } catch {
    // quota/private-mode write failure is ignored: the link-open gate simply reappears next time
  }
}

/** Opens a read-only modal showing the current Terms of Service. */
export function showTermsModal(): void {
  showActionModal(
    t("terms.title"),
    t("terms.body"),
    [{ label: t("terms.close"), value: "close" }],
    (): void => {},
  );
}

/** Opens a read-only modal disclosing how uploaded mic audio is stored, shared, and deleted. */
export function showMicLifecycleModal(): void {
  showActionModal(
    t("upload.micLifecycle.title"),
    [
      t("upload.micLifecycle.public"),
      t("upload.micLifecycle.quota"),
      t("upload.micLifecycle.deleteRecording"),
      t("upload.micLifecycle.cache"),
    ].join("\n\n"),
    [{ label: t("upload.micLifecycle.close"), value: "close" }],
    (): void => {},
  );
}
